import React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import { useAnalyticsEvents, withAnalyticsContext } from '@atlaskit/analytics-next';
import LinkIcon from '@atlaskit/icon/glyph/link';
import { AnalyticsContext, UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { ExperienceSuccess } from '@atlassian/ufo';
import { externalLinksProductExperience } from '../experiences';
import { ExternalLinkItem } from '../external-link-item';
import messages from './messages';
import { ExternalLinksWrapper, ShowMoreButton, LabelWithIcon } from './styled';
import type { ExternalLinksReponse } from 'rest/external-links/types';

export interface Props {
    links: ExternalLinksReponse['links'];
    total: number;
    onShowMoreToggle: () => void;
}

export const EXTERNAL_LINKS_TO_BE_SHOWN = 3;

export const ExternalLinksStateless: React.FC<Props & InjectedIntlProps> = (props) => {
    const {
        links,
        total,
        onShowMoreToggle,
        intl: { formatMessage },
    } = props;

    const { createAnalyticsEvent } = useAnalyticsEvents();

    React.useEffect(() => {
        if (total > 0) {
            const event = createAnalyticsEvent({
                analyticsType: UI_EVENT_TYPE,
                action: 'viewed',
                links_count: total,
            });
            event.fire();
        }
    }, [createAnalyticsEvent, total]);

    if (links.length < 1) {
        return null;
    }

    const doesMoreLinkExists = total > links.length;

    return (
        <AnalyticsContext attributes={{ links_count: total }}>
            <ExternalLinksWrapper>
                <h3>
                    <LabelWithIcon>
                        <LinkIcon label="" />
                        <span>{formatMessage(messages.externalLinksSectionHeader)}</span>
                    </LabelWithIcon>
                </h3>
                <ul>
                    {links.map((link, index) => (
                        <ExternalLinkItem {...link} key={link.id} position={index + 1} />
                    ))}
                </ul>
                {total > EXTERNAL_LINKS_TO_BE_SHOWN && (
                    <ShowMoreButton
                        appearance="link"
                        actionSubjectId={
                            doesMoreLinkExists ? 'show-more-external-resources' : 'show-less-external-resources'
                        }
                        onClick={onShowMoreToggle}
                    >
                        {doesMoreLinkExists
                            ? formatMessage(messages.externalLinksSectionShowMore)
                            : formatMessage(messages.externalLinksSectionShowLess)}
                    </ShowMoreButton>
                )}
                <ExperienceSuccess experience={externalLinksProductExperience} />
            </ExternalLinksWrapper>
        </AnalyticsContext>
    );
};

export default injectIntl(
    withAnalyticsContext({
        component: 'externalResources',
    })(ExternalLinksStateless)
);
